/**
 * Slugify Generator Tool
 *
 * Convert text to URL-friendly slugs.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const SlugifyGeneratorTool: ITransformerTool = {
  id: 'slugify-generator',
  name: 'Slugify',
  description: 'Convert text to URL-friendly slugs',
  version: '1.0.0',
  type: 'transformer',
  category: 'Generators',
  keywords: ['slug', 'slugify', 'url', 'friendly', 'permalink', 'seo'],

  seo: {
    slug: 'slugify-generator',
    pageTitle: 'Slugify - Free Online URL Slug Generator',
    metaDescription: 'Convert text to URL-friendly slugs instantly. Configurable separator, case, and max length. Fast, private, runs entirely in your browser.',
    keywords: ['slugify', 'url slug generator', 'slug generator online', 'permalink generator', 'seo url tool'],
    relatedTools: ['case-converter', 'url-encode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'separator',
      type: 'select',
      label: 'Separator',
      defaultValue: '-',
      options: ['-', '_'],
      description: 'Character to replace spaces/special chars',
    },
    {
      name: 'lowercase',
      type: 'boolean',
      label: 'Lowercase',
      defaultValue: true,
      description: 'Convert to lowercase',
    },
    {
      name: 'maxLength',
      type: 'number',
      label: 'Max Length',
      defaultValue: 0,
      validation: { min: 0, max: 500 },
      description: 'Maximum slug length (0 = no limit)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const separator = String(config.separator ?? '-');
    const lowercase = config.lowercase !== false;
    const maxLength = Number(config.maxLength ?? 0);

    const lines = input.split('\n');
    const slugs = lines.map(line => {
      let slug = line
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
        .replace(/[^\w\s-]/g, '')         // Remove non-word chars (except spaces and hyphens)
        .replace(/[\s_-]+/g, separator)   // Replace whitespace/underscores/hyphens with separator
        .replace(new RegExp(`^${escapeRegExp(separator)}+|${escapeRegExp(separator)}+$`, 'g'), ''); // Trim separators

      if (lowercase) slug = slug.toLowerCase();
      if (maxLength > 0 && slug.length > maxLength) {
        slug = slug.slice(0, maxLength).replace(new RegExp(`${escapeRegExp(separator)}+$`), '');
      }

      return slug;
    });

    return slugs.join('\n');
  },
};

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
